/**
 * VqeStarterCanvas — the starter canvas for a blank VQE buffer (Task 4, §3.2
 * S9): pick a table and `onSeedQuery` writes `SELECT * FROM <table>` into the
 * buffer, after which classification re-runs and the normal SELECT renderer
 * mounts — no `commitEdit` (there is no statement yet to anchor a structural
 * op on). The host decides how the buffer is written: the full tab writes its
 * store content, the Query Editor's Visual mode writes through Monaco so the
 * seed is one undo stop.
 *
 * Readiness follows the standard RO contract (decision 4): the table picker
 * stays `disabled` + reason-tooltip until a connection AND a schema snapshot
 * are present — reusing `vqeCopy.disabled.noConnection` / `.noSnapshot`
 * verbatim (no parallel copy).
 *
 * Empty-schema honesty (T3 review follow-up): "the schema snapshot has
 * loaded" and "the schema snapshot has tables" are DIFFERENT facts. This
 * canvas reads `service.getSchemaSnapshot()` directly, so once the fetch has
 * completed (defined snapshot, regardless of table count) the picker itself
 * ENABLES — an empty database shows the honest `vqeCopy.canvas.noTables`
 * message (both inline here and as the picker's own `emptyMessage`), never a
 * "still loading" tooltip that has stopped being true. The workspace banner
 * follows the same rule (a landed empty snapshot is `ready`).
 *
 * The empty-database message carries its two ways out as small outline
 * buttons on one line: Reload schema (tables created since the schema loaded —
 * the same verb as the readiness banner) and Choose another database (opens
 * the header's database picker). SQLite has one database per connection, so
 * it gets Reload only.
 *
 * A FAILED table list swaps the body for `vqeCopy.canvas.loadFailed` and hides
 * the table picker: the banner above owns Reload schema, and a disabled
 * "Start from a table" would only repeat that nothing can be picked.
 *
 * "Start from a table" is the canvas's main action, so its trigger is the
 * filled `primary` variant; Start from SQL stays outlined, and only the
 * disabled state (no connection / still loading) is muted.
 */
import { useMemo } from 'react';

import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { Button } from '../../../../components/ui/button';
import type { SqlEngine } from '../../../sql-grammar/model';
import type { SqlResolverService } from '../../../sql-resolver/service/resolverService';
import { CommandPickerPopover } from '../render/common/CommandPickerPopover';
import type { VqeReadinessState } from '../ux/VqeReadinessBanner';
import { vqeCopy } from '../ux/vqeCopy';
import { buildStarterSelect, starterTableGroups } from './starterTableGroups';

export interface VqeStarterCanvasProps {
  /** Write the seeded statement into the host's buffer. */
  readonly onSeedQuery: (sql: string) => void;
  /** ALWAYS present (per `useVqeEditWiring`'s `VqeTabWiring` contract). */
  readonly service: SqlResolverService;
  readonly engine: SqlEngine;
  /** The workspace's own readiness derivation — used to detect the
   *  no-connection and failed-load cases; the loaded-vs-empty schema
   *  distinction is derived locally from `service.getSchemaSnapshot()` (see
   *  file header). */
  readonly readinessState: VqeReadinessState;
  /** Escape hatch — mirrors the workspace toolbar's "Edit as SQL" action. */
  readonly onStartFromSql: () => void;
  /** Empty database → re-read the schema (the readiness hook's reloadSchema). */
  readonly onRefreshSchema?: () => void;
  /** Empty database → open the header database picker. Omitted where there is nothing to switch to. */
  readonly onChooseDatabase?: () => void;
}

export function VqeStarterCanvas({
  onSeedQuery,
  service,
  engine,
  readinessState,
  onStartFromSql,
  onRefreshSchema,
  onChooseDatabase,
}: VqeStarterCanvasProps) {
  const { track } = useAnalytics();
  const snapshot = service.getSchemaSnapshot() ?? null;
  const hasConnection = readinessState !== 'no-connection';
  const snapshotLoaded = snapshot !== null;
  // A failed table list also lands as a snapshot with no tables — the banner
  // above says why, so the canvas must not claim the database is empty.
  const loadFailed = readinessState === 'snapshot-failed';
  const isEmptySchema = snapshotLoaded && snapshot.tables.size === 0 && !loadFailed;

  const groups = useMemo(() => starterTableGroups(snapshot), [snapshot]);
  const tableByKey = useMemo(() => {
    const map = new Map<string, (typeof groups)[number]['items'][number]['table']>();
    for (const group of groups) {
      for (const item of group.items) map.set(item.key, item.table);
    }
    return map;
  }, [groups]);

  const disabled = !hasConnection || !snapshotLoaded;
  const tooltip = !hasConnection
    ? vqeCopy.disabled.noConnection
    : !snapshotLoaded
      ? vqeCopy.disabled.noSnapshot
      : undefined;

  const handleSelect = (key: string) => {
    const table = tableByKey.get(key);
    if (!table) return;
    onSeedQuery(buildStarterSelect(table, service.engineId));
    // engine only — never the table name (analytics rule, no PII/high-cardinality props).
    track('feature:vqe_canvas_seed', { engine });
  };

  const emptyAction = (action: 'refresh_schema' | 'choose_database', run: () => void) => () => {
    track('feature:vqe_canvas_empty_action', { action, engine });
    run();
  };

  return (
    <div className="flex flex-1 items-center justify-center px-6 py-8">
      <div
        className="max-w-md rounded-lg border border-border bg-card p-6 text-center text-sm text-muted-foreground shadow-sm"
        data-testid="vqe-starter-canvas"
      >
        <p className="font-medium text-foreground">{vqeCopy.canvas.title}</p>
        <p className="mt-2" data-testid="vqe-canvas-body">
          {loadFailed ? vqeCopy.canvas.loadFailed : vqeCopy.canvas.body}
        </p>
        {isEmptySchema ? (
          <div className="mt-3">
            <p data-testid="vqe-canvas-empty-schema">{vqeCopy.canvas.emptyDatabase}</p>
            <div className="mt-2 flex items-center justify-center gap-2">
              {onRefreshSchema ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={emptyAction('refresh_schema', onRefreshSchema)}
                  data-testid="vqe-canvas-refresh-schema"
                >
                  {vqeCopy.canvas.reloadSchema}
                </Button>
              ) : null}
              {onChooseDatabase ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={emptyAction('choose_database', onChooseDatabase)}
                  data-testid="vqe-canvas-choose-database"
                >
                  {vqeCopy.canvas.chooseDatabase}
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
        <div className="mt-4 flex items-center justify-center gap-2">
          {loadFailed ? null : (
            <CommandPickerPopover
              testId="vqe-canvas-table-picker"
              triggerLabel={vqeCopy.canvas.startFromTable}
              triggerVariant="primary"
              groups={groups}
              disabled={disabled}
              tooltip={tooltip}
              onSelect={handleSelect}
              searchPlaceholder={vqeCopy.canvas.searchPlaceholder}
              emptyMessage={vqeCopy.canvas.noTables}
            />
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onStartFromSql}
            data-testid="vqe-canvas-start-from-sql"
          >
            {vqeCopy.canvas.startFromSql}
          </Button>
        </div>
      </div>
    </div>
  );
}
